import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading?: boolean;
  /** Error message returned by the delete-account function. */
  error?: string | null;
}
const CONFIRM_WORD = 'DELETE';
export function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  error = null
}: DeleteAccountModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    setConfirmText('');
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  if (!isOpen) return null;
  const isConfirmValid = confirmText.trim() === CONFIRM_WORD;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConfirmValid || loading) return;
    await onConfirm();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => !loading && onClose()} data-id="element-2002" data-mp="1b94qehiprf0">
      <div role="dialog" aria-modal="true" aria-labelledby="delete-account-title" aria-describedby="delete-account-desc" onClick={e => e.stopPropagation()} className="relative max-h-[90vh] w-full max-w-md overflow-y-auto border border-[#FF0000]/40 bg-[#0A0A0A] shadow-2xl" data-id="element-2003" data-mp="3nrws37j90vsm">
        <form onSubmit={handleSubmit} className="p-6 sm:p-8" data-id="element-2004" data-mp="39r2joow7auty">
          <div className="mb-5 flex items-start justify-between gap-4" data-id="element-2005" data-mp="34t10m3yryvih">
            <div className="flex items-center gap-3" data-id="element-2006" data-mp="34tsv1bgmyn76">
              <span className="flex h-10 w-10 items-center justify-center border border-[#FF0000]/40 bg-[#FF0000]/10" data-id="element-2007" data-mp="2v55hc2b72nfy">
                <AlertTriangle size={18} className="text-[#FF0000]" aria-hidden="true" data-id="element-2008" data-mp="305b2czmylbp2" />
              </span>
              <h2 id="delete-account-title" className="text-lg font-black uppercase tracking-tight text-white" data-id="element-2009" data-mp="2m6bf9wf3h4v0">
                Delete account
              </h2>
            </div>
            <button type="button" onClick={onClose} disabled={loading} aria-label="Close" className="p-1 text-gray-500 transition-colors duration-150 ease-out hover:text-white disabled:opacity-40" data-id="element-2010" data-mp="3dy93z3pzw012">
              <X size={20} data-id="element-2011" data-mp="393ddi80epkq2" />
            </button>
          </div>

          <p id="delete-account-desc" className="text-sm leading-relaxed text-gray-300" data-id="element-2012" data-mp="obq8xc79uw4">
            This permanently deletes your account, scans and uploaded screenshots,
            and cancels any subscription. Payments already made are not refunded.
            This cannot be undone.
          </p>

          <label htmlFor="delete-confirm" className="mt-6 block text-sm text-gray-400" data-id="element-2013" data-mp="1l6dyh3l4upp9">
            Type <span className="font-mono font-bold text-white" data-id="element-2014" data-mp="1zva0aiwlj6hf">{CONFIRM_WORD}</span> to confirm
          </label>
          <input ref={inputRef} id="delete-confirm" type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} disabled={loading} autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder={CONFIRM_WORD} className="mt-2 w-full border border-gray-800 bg-[#050505] px-4 py-3 font-mono text-sm text-white placeholder-gray-700 transition-colors duration-150 ease-out focus:border-[#FF0000] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50" data-id="element-2015" data-mp="1uyjmk289kmz6" />

          {error && !loading && <p role="alert" className="mt-4 flex items-start gap-2 border border-[#FF0000]/30 bg-[#FF0000]/10 p-3 text-sm text-[#FF6B6B]" data-id="element-2016" data-mp="x5k8uv62rrnv">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" data-id="element-2017" data-mp="165qj886h8bit" />
              {error}
            </p>}

          {loading && <p role="status" aria-live="polite" className="mt-4 flex items-center gap-2 text-sm text-gray-300" data-id="element-2018" data-mp="1b5v1o3qy3gbo">
              <Loader2 size={15} className="animate-spin" aria-hidden="true" data-id="element-2019" data-mp="1l1pozc645zks" />
              Deleting your account…
            </p>}

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row" data-id="element-2020" data-mp="1pvke4v4hn6r9">
            <button type="button" onClick={onClose} disabled={loading} className="flex-1 border border-gray-800 px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-300 transition-colors duration-150 ease-out hover:border-gray-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50" data-id="element-2021" data-mp="1tujjuhy1n7jr">
              Cancel
            </button>
            <button type="submit" disabled={!isConfirmValid || loading} className="flex flex-1 items-center justify-center gap-2 bg-[#FF0000] px-4 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 ease-out hover:bg-[#D90000] disabled:cursor-not-allowed disabled:opacity-40" data-id="element-2022" data-mp="2905t7bd0cfy5">
              {loading ? 'Deleting your account…' : 'Delete account'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}